import React, { useState } from 'react';
import {
  Table,
  Button,
  Select,
  Tag,
  Row,
  Col,
  Space,
  Popconfirm,
  message,
  Result,
} from 'antd';
import { PlusOutlined, ReloadOutlined, EditOutlined } from '@ant-design/icons';
import type { TableColumnsType } from 'antd';
import Card from 'components/card';
import { useMarketplaces } from 'services/MarketplaceService';
import { useStoresByMarketplace } from 'services/StoreService';
import {
  CollectionPoint,
  collectionsResultStatus,
  useCollectionPoints,
  useSetCollectionPointActive,
} from 'services/CollectionsV2Service';
import { formatAmount, hasAmount } from 'util/amount';
import { collectionAmountsEnabled } from 'constants/flags';
import CollectionPointFormModal from './CollectionPointFormModal';

const PAGE_SIZE = 20;

const ACTIVE_OPTIONS = [
  { label: 'Activos', value: 'true' },
  { label: 'Inactivos', value: 'false' },
  { label: 'Todos', value: '' },
];

const CollectionPoints: React.FC = () => {
  // Con selectores y no texto libre, igual que Solicitudes y que el listado de colectas.
  // Acá no hay driver: el punto es un lugar, no una visita.
  const [marketplaceId, setMarketplaceId] = useState<number | null>(null);
  const [storeId, setStoreId] = useState<number | null>(null);
  const [active, setActive] = useState<string>('true');
  const [page, setPage] = useState(0);
  const [editing, setEditing] = useState<CollectionPoint | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const { data: marketplaces = [], isLoading: marketplacesLoading } =
    useMarketplaces();
  const { data: stores = [], isLoading: storesLoading } =
    useStoresByMarketplace(marketplaceId ?? 0);

  const marketplaceOptions = marketplaces.map((m) => ({
    label: m.name,
    value: m.id,
  }));
  const storeOptions = stores.map((s) => ({ label: s.name, value: s.id }));

  const filters = {
    marketplaceId: marketplaceId ?? undefined,
    storeId: storeId ?? undefined,
    active: active === '' ? undefined : active === 'true',
    page,
    pageSize: PAGE_SIZE,
  };

  const { data, isFetching, error, refetch } = useCollectionPoints(filters);
  const { mutateAsync: setPointActive, isPending: isToggling } =
    useSetCollectionPointActive();

  const toggleActive = async (point: CollectionPoint) => {
    try {
      await setPointActive({ id: point.id, active: !point.active });
      message.success(point.active ? 'Punto desactivado' : 'Punto reactivado');
    } catch (err: any) {
      message.error(err?.message ?? 'No se pudo cambiar el estado');
    }
  };

  const openNew = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (point: CollectionPoint) => {
    setEditing(point);
    setModalOpen(true);
  };

  const columns: TableColumnsType<CollectionPoint> = [
    { title: '#', dataIndex: 'id', key: 'id', width: 70 },
    {
      title: 'Tienda',
      key: 'store',
      render: (_value, record) => (
        <Space direction="vertical" size={0}>
          <Tag color="blue" style={{ margin: 0 }}>
            {record.storeName}
          </Tag>
          <span style={{ fontSize: 12 }}>{record.marketplaceName}</span>
        </Space>
      ),
    },
    { title: 'Dirección', dataIndex: 'address', key: 'address' },
    {
      title: 'Turnos',
      dataIndex: 'shifts',
      key: 'shifts',
      width: 220,
      render: (shifts: CollectionPoint['shifts']) =>
        shifts?.length ? (
          <Space size={4} wrap>
            {shifts.map((shift) => (
              <Tag key={shift.id} style={{ margin: 0 }}>
                {shift.startTime} · {shift.estimatedDurationMinutes} min
              </Tag>
            ))}
          </Space>
        ) : (
          <span style={{ color: '#8c8c8c' }}>Sin turnos</span>
        ),
    },
    ...(collectionAmountsEnabled
      ? [
          {
            title: 'Valor',
            dataIndex: 'amount',
            key: 'amount',
            width: 130,
            align: 'right' as const,
            render: (amount?: number | null) => (
              <span
                style={hasAmount(amount) ? undefined : { color: '#8c8c8c' }}
              >
                {formatAmount(amount)}
              </span>
            ),
          },
        ]
      : []),
    {
      title: 'Estado',
      dataIndex: 'active',
      key: 'active',
      width: 110,
      render: (value: boolean) => (
        <Tag color={value ? 'green' : 'default'}>
          {value ? 'Activo' : 'Inactivo'}
        </Tag>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      align: 'right',
      width: 230,
      render: (_value, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEdit(record)}
          >
            Editar
          </Button>
          <Popconfirm
            title={record.active ? 'Desactivar el punto' : 'Reactivar el punto'}
            description={
              record.active
                ? 'Sale del selector del ruteador. Las colectas que ya se crearon desde este punto no se tocan.'
                : 'Vuelve a estar disponible en el ruteador.'
            }
            okText={record.active ? 'Desactivar' : 'Reactivar'}
            cancelText="Cancelar"
            onConfirm={() => toggleActive(record)}
          >
            <Button size="small" danger={record.active} loading={isToggling}>
              {record.active ? 'Desactivar' : 'Reactivar'}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card title="Puntos de Colecta">
      <Row gutter={[8, 8]} align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={() => refetch()}
            loading={isFetching}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Select
            style={{ width: '100%' }}
            options={marketplaceOptions}
            value={marketplaceId}
            onChange={(value) => {
              setPage(0);
              setMarketplaceId(value);
              setStoreId(null);
            }}
            placeholder="Filtrar por marketplace"
            loading={marketplacesLoading}
            allowClear
            showSearch
            optionFilterProp="label"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Select
            style={{ width: '100%' }}
            options={storeOptions}
            value={storeId}
            onChange={(value) => {
              setPage(0);
              setStoreId(value);
            }}
            placeholder="Filtrar por tienda"
            loading={storesLoading}
            disabled={!marketplaceId}
            allowClear
            showSearch
            optionFilterProp="label"
          />
        </Col>
        <Col>
          <Select
            style={{ width: 160 }}
            value={active}
            options={ACTIVE_OPTIONS}
            onChange={(value) => {
              setPage(0);
              setActive(value);
            }}
          />
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={openNew}>
            Nuevo Punto
          </Button>
        </Col>
      </Row>

      {error ? (
        <Result
          status={collectionsResultStatus(error)}
          title="Algo salió mal..."
          subTitle={(error as Error).message}
          extra={[
            <Button type="primary" key="retry" onClick={() => refetch()}>
              Reintentar
            </Button>,
          ]}
        />
      ) : (
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data?.items ?? []}
          loading={isFetching}
          pagination={{
            current: page + 1,
            pageSize: PAGE_SIZE,
            total: data?.totalElements ?? 0,
            showSizeChanger: false,
            onChange: (nextPage) => setPage(nextPage - 1),
          }}
        />
      )}

      <CollectionPointFormModal
        open={modalOpen}
        point={editing}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
      />
    </Card>
  );
};

export default CollectionPoints;
